import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// The exclusions of the user reports: who is left out of the figures and the
// lists. Same shape the userReports function takes; `users` holds users doc
// ids. The backend does the matching (no accents, any case), so this editor
// only collects what to match.
export const EMPTY_EXCLUSIONS = { rules: [], emailDomains: [], users: [] };

// The rules that would exclude someone: a rule with no values yet is ignored
// (by the backend too).
const effectiveRules = (rules) => rules.filter((r) => r.op === "empty" || r.values.length > 0);

export const exclusionCount = (ex) =>
  effectiveRules(ex.rules).length + ex.emailDomains.length + ex.users.length;

// What is sent: half-filled rules dropped.
export const exclusionsForRequest = (ex) => ({
  rules: effectiveRules(ex.rules).map(({ field, op, values }) => ({ field, op, values: op === "empty" ? [] : values })),
  emailDomains: ex.emailDomains,
  users: ex.users,
});

const OP_LABELS = { equals: "es igual a", contains: "contiene", empty: "está vacío" };

// The fields a rule can read, in the order of the report's columns. `values`
// says where the suggestions come from; role and type only take known values.
const FIELDS = [
  { key: "dependence", label: "Dependencia", ops: ["equals", "contains", "empty"] },
  { key: "level", label: "Área (nivel)", ops: ["equals", "contains", "empty"] },
  { key: "position", label: "Cargo", ops: ["equals", "contains", "empty"] },
  { key: "city", label: "Sede", ops: ["equals", "contains", "empty"] },
  { key: "entity", label: "Institución", ops: ["equals", "contains", "empty"] },
  { key: "usertitle", label: "Título", ops: ["equals", "contains", "empty"] },
  { key: "email", label: "Correo", ops: ["contains", "equals", "empty"] },
  { key: "type", label: "Tipo de cuenta", ops: ["equals"] },
  { key: "role", label: "Rol", ops: ["equals", "empty"], opLabels: { equals: "tiene el rol", empty: "no tiene rol" } },
];
const FIELD_INFO = Object.fromEntries(FIELDS.map((f) => [f.key, f]));
const TYPE_VALUES = ["Funcionario", "Empresa"];

const fmtInt = (n) => new Intl.NumberFormat("es-CO").format(n || 0);

// Chips that the backend would match alike ("royal", "ROYAL", "Róyal ")
// collapse into the first one typed.
const foldKey = (v) =>
  String(v).normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim().toUpperCase();
const uniqueValues = (list) => {
  const seen = new Set();
  return list
    .map((v) => String(v).replace(/\s+/g, " ").trim())
    .filter((v) => v && !seen.has(foldKey(v)) && seen.add(foldKey(v)));
};

// Free-text chips: typed text becomes a chip on Enter, and also on leaving the
// field (autoSelect), so a value typed but not confirmed is not lost. Chosen
// values leave the suggestions (filterSelectedOptions): picking one again
// would otherwise toggle its chip off.
function ValuesInput({ rule, options, roleOptions, onChange }) {
  if (rule.field === "role") {
    const byKey = new Map(roleOptions.map((o) => [o.key, o]));
    return (
      <Autocomplete
        multiple
        size="small"
        options={roleOptions}
        groupBy={(o) => o.group}
        value={rule.values.map((k) => byKey.get(k) || { key: k, label: k, group: "" })}
        onChange={(_, v) => onChange(v.map((o) => o.key))}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.key === b.key}
        renderInput={(params) => <TextField {...params} label="Roles" />}
      />
    );
  }
  if (rule.field === "type") {
    return (
      <Autocomplete
        multiple
        size="small"
        options={TYPE_VALUES}
        value={rule.values}
        onChange={(_, v) => onChange(v)}
        renderInput={(params) => <TextField {...params} label="Tipo" />}
      />
    );
  }
  const suggestions =
    rule.field === "dependence"
      ? (options?.dependencies || []).filter((d) => d.key).map((d) => ({ label: d.label, count: d.count }))
      : options?.values?.[rule.field] || [];
  return (
    <Autocomplete
      multiple
      freeSolo
      autoSelect
      filterSelectedOptions
      size="small"
      options={suggestions.map((s) => s.label)}
      value={rule.values}
      onChange={(_, v) => onChange(uniqueValues(v))}
      renderOption={(props, label) => {
        const count = suggestions.find((s) => s.label === label)?.count;
        return (
          <li {...props} key={label}>
            {label}
            {count ? ` (${fmtInt(count)})` : ""}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Valores"
          placeholder={rule.values.length ? "" : rule.op === "contains" ? "Escriba un texto y Enter" : "Elija o escriba"}
        />
      )}
    />
  );
}

export default function UserReportExclusions({ value, onChange, options, roleOptions, appliesTo }) {
  const setRules = (rules) => onChange({ ...value, rules });
  const updateRule = (i, patch) => setRules(value.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRule = () => setRules([...value.rules, { field: "entity", op: "contains", values: [] }]);

  const userOptions = options?.users || [];
  const userById = new Map(userOptions.map((u) => [u.id, u]));
  const pickedUsers = value.users.map(
    (id) => userById.get(id) || { id, name: "Usuario no encontrado", email: id, dependence: "" }
  );
  const userLabel = (u) => [u.name, u.email].filter(Boolean).join(" · ") || u.id;
  const domainCounts = new Map((options?.values?.emailDomain || []).map((d) => [d.label, d.count]));

  return (
    <Box>
      <Typography variant="subtitle2">Excluir usuarios</Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
        Quien cumpla cualquiera de estas condiciones queda fuera {appliesTo}. Las comparaciones no distinguen
        mayúsculas ni tildes.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            multiple
            freeSolo
            autoSelect
            filterSelectedOptions
            size="small"
            options={[...domainCounts.keys()]}
            value={value.emailDomains}
            onChange={(_, v) =>
              onChange({
                ...value,
                emailDomains: [
                  ...new Set(v.map((d) => String(d).trim().toLowerCase().replace(/^@+/, "")).filter(Boolean)),
                ],
              })
            }
            renderOption={(props, d) => (
              <li {...props} key={d}>
                @{d} ({fmtInt(domainCounts.get(d))})
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Dominios de correo"
                placeholder={value.emailDomains.length ? "" : "p. ej. royalsoftware.com.co"}
                helperText="Incluye sus subdominios. Un dominio mal escrito es otro dominio: agréguelo también."
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            multiple
            size="small"
            options={userOptions}
            value={pickedUsers}
            onChange={(_, v) => onChange({ ...value, users: v.map((u) => u.id) })}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              const hits = q
                ? opts.filter((u) => `${u.name} ${u.email} ${u.dependence}`.toLowerCase().includes(q))
                : opts;
              return hits.slice(0, 50);
            }}
            renderOption={(props, u) => (
              <li {...props} key={u.id}>
                <Box>
                  <Typography variant="body2">{u.name || "(sin nombre)"}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[u.email, u.dependence].filter(Boolean).join(" · ") || u.id}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Usuarios"
                placeholder={value.users.length ? "" : "Buscar por nombre, correo o dependencia"}
              />
            )}
            noOptionsText={options ? "Sin coincidencias" : "Cargando usuarios…"}
          />
        </Grid>

        {value.rules.map((rule, i) => {
          const info = FIELD_INFO[rule.field];
          return (
            <React.Fragment key={i}>
              <Grid item xs={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Campo"
                  value={rule.field}
                  onChange={(e) => {
                    const next = FIELD_INFO[e.target.value];
                    // Values of one field mean nothing in another.
                    updateRule(i, {
                      field: next.key,
                      op: next.ops.includes(rule.op) ? rule.op : next.ops[0],
                      values: [],
                    });
                  }}
                >
                  {FIELDS.map((f) => (
                    <MenuItem key={f.key} value={f.key}>
                      {f.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Condición"
                  value={rule.op}
                  onChange={(e) => updateRule(i, { op: e.target.value })}
                >
                  {info.ops.map((op) => (
                    <MenuItem key={op} value={op}>
                      {info.opLabels?.[op] || OP_LABELS[op]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={10} md={6}>
                {rule.op !== "empty" && (
                  <ValuesInput
                    rule={rule}
                    options={options}
                    roleOptions={roleOptions}
                    onChange={(values) => updateRule(i, { values })}
                  />
                )}
              </Grid>
              <Grid item xs={2} md={1} sx={{ display: "flex", alignItems: "flex-start" }}>
                <Tooltip title="Quitar regla">
                  <IconButton
                    aria-label="Quitar regla"
                    onClick={() => setRules(value.rules.filter((_, j) => j !== i))}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </React.Fragment>
          );
        })}
        <Grid item xs={12} sx={{ pt: "8px !important" }}>
          <Button size="small" startIcon={<AddIcon />} onClick={addRule}>
            Agregar regla por campo
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
